import type { ControlRoomJob } from "./service";

/**
 * The pure "which bucket does this job fall into" logic behind Control Room's rail sections
 * (`ControlRoomScreen.tsx`'s `tabForJob`/`RAIL_SECTION_ORDER`) — pulled out into its own module
 * (rather than staying private to that screen) so `SpotterHeader.tsx`'s Control Room tab badge
 * (`useControlRoomNeedsAttention`, `controlRoom/service.ts`) can ask "does anything need
 * attention?" using the EXACT SAME precedence the rail itself uses, without a second, drifting
 * copy of it and without `SpotterHeader.tsx` importing from `ControlRoomScreen.tsx` (which
 * already imports `SpotterHeader` — a cycle). `ControlRoomScreen.tsx` re-exports the names its
 * own tests already import from it, so nothing there had to change shape.
 */

/**
 * The one thing about a job that decides both its status pill (`ControlRoomScreen.tsx`'s
 * `stageDisplay`) AND its rail section (`tabForJob`) — a single source of truth for the
 * precedence, so the two never drift apart: an open anchor-job review (`hasReview &&
 * reviewNeedsInput`, Monday 12921515105) wins over EVERYTHING else, including `done` — a
 * `game`/`sideline`/etc. pipeline reaching `done` proves nothing about whether that separate
 * review track was ever resolved (the exact bug this ranking exists to fix: a slow-mo-band job
 * with 20 unconfirmed anchors must never sort into "Done"/show a "Complete" pill just because its
 * OWN pipeline finished). Only below that does the pipeline's own `done` win (the customer's own
 * "done is final, never regress it" decision), THEN an `error` status (more actionable than a
 * stale `needsInput` latch), THEN the `needsInput` latch, else the job is just moving through its
 * ranked stages (or hasn't reached one yet). `hidden` (`tabForJob`) is decided BEFORE this ever
 * runs, not as part of it — see that function's own comment.
 */
export type PrimaryJobState =
  | "needsReview"
  | "done"
  | "error"
  | "needsInput"
  | "stage";

export function primaryJobState(job: ControlRoomJob): PrimaryJobState {
  if (job.hasReview && job.reviewNeedsInput) {
    return "needsReview";
  }
  if (job.latestStage === "done") {
    return "done";
  }
  if (job.latestStatus === "error") {
    return "error";
  }
  if (job.needsInput) {
    return "needsInput";
  }
  return "stage";
}

/** Seth (Monday 12917797269): "just hide jobs after 24 hours, don't mark them expired" — a flat
 *  rule with no state-based exemptions. One named constant since he may want it tuned. */
export const STALE_JOB_HIDE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

// A malformed or absent `unhiddenAt` must never win the `Math.max` below (NaN would, silently
// making `isStale` permanently false) — parses to a real epoch-ms number, or `0` if there's
// nothing usable, which is the same as never having been unhidden.
function unhiddenAtMs(job: ControlRoomJob): number {
  if (!job.unhiddenAt) {
    return 0;
  }
  const ms = new Date(job.unhiddenAt).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

// Restart, not exemption (Monday 12952714235, Addison: "should just restart timer") — an explicit
// unhide moves the staleness baseline forward to whenever it happened, same as a fresh push would.
// A future `unhiddenAt` (clock skew, bad data) only pushes the baseline out to that point and
// self-heals once real time passes it — it can't make a job permanently visible.
export function isStale(job: ControlRoomJob): boolean {
  const baseline = Math.max(
    new Date(job.lastUpdatedAt).getTime(),
    unhiddenAtMs(job)
  );
  return Date.now() - baseline > STALE_JOB_HIDE_THRESHOLD_MS;
}

/** Rail section a job falls into (Monday 12921148830, reformatted into the rail by design A4) —
 *  no dedicated "error" section: an errored job (not yet done) still counts as "still processing"
 *  here, same as it isn't its own row in `MAIN_STAGES` either. */
export type ControlRoomTabKey =
  | "all"
  | "needsYou"
  | "processing"
  | "done"
  | "hidden";

export function tabForJob(
  job: ControlRoomJob
): Exclude<ControlRoomTabKey, "all"> {
  // Wins over every other bucket, unconditionally — a hidden job must land under `hidden` and
  // NOWHERE else, even if it still has an open review (this item's own verify point). Checked
  // before `primaryJobState` even runs, not folded into its precedence. Staleness is derived from
  // `lastUpdatedAt`/`unhiddenAt`, not persisted itself, so a stale job that gets a fresh push (or
  // an unhide) rejoins its normal section on its own — no self-healing special-case needed.
  if (job.dismissed || isStale(job)) {
    return "hidden";
  }
  const state = primaryJobState(job);
  if (state === "needsReview" || state === "needsInput") {
    return "needsYou";
  }
  if (state === "done") {
    return "done";
  }
  return "processing";
}

export type HideButtonMode = "hide" | "unhide";

/** The rail row's hide/unhide control mode — driven by which bucket the row is ACTUALLY in
 *  (`tabForJob`), not by `job.dismissed` alone: a stale-only row (`dismissed: false`) is under
 *  Hidden too and must offer Unhide the same as a manually hidden one. */
export function hideButtonMode(job: ControlRoomJob): HideButtonMode {
  return tabForJob(job) === "hidden" ? "unhide" : "hide";
}
